'use client';
import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: string;
  name: string;
  company: string;
  country: string;
  countryFlag: string;
  quote: string;
  photo: string;
  verified: boolean;
}

const TestimonialsSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const testimonials: Testimonial[] = [
  {
    id: 'testimonial-1',
    name: 'Ahmed Al-Mansouri',
    company: 'Al-Etihad Gold Refinery',
    country: 'UAE',
    countryFlag: '🇦🇪',
    quote:
    'Reliable partner for our Dubai refinery. Consistent quality and transparent pricing. We have completed over 50 shipments with Mulumbu Minerals and never had any issues.',
    photo:
    "https://img.rocket.new/generatedImages/rocket_gen_img_1ff70f3ee-1765607087635.png",
    verified: true
  },
  {
    id: 'testimonial-2',
    name: 'Chen Wei',
    company: 'Shanghai Jewelry Co.',
    country: 'China',
    countryFlag: '🇨🇳',
    quote:
    'Smooth customs process and excellent communication. Highly recommend for international buyers. Their transparency and professionalism set them apart from other suppliers.',
    photo:
    "https://img.rocket.new/generatedImages/rocket_gen_img_1d98b9f21-1770602853016.png",
    verified: true
  },
  {
    id: 'testimonial-3',
    name: 'Johann Schmidt',
    company: 'Swiss Gold Trading AG',
    country: 'Switzerland',
    countryFlag: '🇨🇭',
    quote:
    'Best gold supplier in Central Africa. Fair prices and fast shipping. We have been working with Mulumbu Minerals for 3 years and they never disappoint.',
    photo:
    "https://img.rocket.new/generatedImages/rocket_gen_img_1e7fdc040-1768549556924.png",
    verified: true
  }];


  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % testimonials.length);
        setIsTransitioning(false);
      }, 300);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const activeTestimonial = testimonials[activeIndex];

  const handleDotClick = (index: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex(index);
      setIsTransitioning(false);
    }, 300);
  };

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-background">
      <div className="w-full max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20 reveal">
          <span className="text-xs font-body font-bold uppercase tracking-[0.4em] text-muted-foreground mb-4 block">
            Client Testimonials
          </span>
          <h2 className="text-5xl md:text-6xl font-display font-bold tracking-tight text-foreground mb-4">
            Trusted by International Buyers
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Verified testimonials from our global partners across UAE, China, and Switzerland.
          </p>
        </div>

        {/* Testimonial Display */}
        <div className="relative h-auto md:h-[600px] flex items-center justify-center reveal">
          {/* Overlapping Avatar Cards with Enhanced Hover */}
          <div className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 -rotate-12 w-48 h-64 rounded-3xl overflow-hidden shadow-xl transition-smooth hover:scale-110 hover:rotate-0 hover:z-30">
            <AppImage
              src={testimonials[0].photo}
              alt={`${testimonials[0].name} from ${testimonials[0].company}`}
              className="w-full h-full object-cover grayscale hover:grayscale-0 transition-smooth" />
            
          </div>
          <div className="hidden md:block absolute left-24 top-1/2 -translate-y-1/3 rotate-6 w-48 h-64 rounded-3xl overflow-hidden shadow-xl transition-smooth hover:scale-110 hover:rotate-0 hover:z-30">
            <AppImage
              src={testimonials[1].photo}
              alt={`${testimonials[1].name} from ${testimonials[1].company}`}
              className="w-full h-full object-cover grayscale hover:grayscale-0 transition-smooth" />
            
          </div>

          {/* Center Quote Card with Fade Transition */}
          <div className={`relative z-10 w-full max-w-2xl glass-card p-8 md:p-16 rounded-3xl shadow-2xl text-center transition-smooth hover-lift hover-glow ${isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
            {/* Avatar */}
            <div className="w-24 h-24 mx-auto mb-6 rounded-2xl overflow-hidden border-4 border-card shadow-lg transition-smooth hover:scale-110 hover:rotate-6">
              <AppImage
                src={activeTestimonial.photo}
                alt={`${activeTestimonial.name} from ${activeTestimonial.company}`}
                className="w-full h-full object-cover" />
              
            </div>

            {/* Name & Company */}
            <h4 className="text-2xl font-display font-bold text-foreground mb-1">
              {activeTestimonial.name}
            </h4>
            <div className="flex items-center justify-center gap-2 mb-2">
              <p className="text-sm font-body text-muted-foreground">
                {activeTestimonial.company}
              </p>
              {activeTestimonial.verified &&
              <Icon name="CheckBadgeIcon" size={18} className="text-primary" />
              }
            </div>
            <p className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-8">
              {activeTestimonial.countryFlag} {activeTestimonial.country}
            </p>

            {/* Quote Mark */}
            <div className="text-6xl font-accent text-muted-foreground/30 mb-6 italic">"</div>

            {/* Quote */}
            <p className="text-lg md:text-xl font-body font-medium text-foreground leading-relaxed mb-8">
              {activeTestimonial.quote}
            </p>

            {/* Navigation Dots with Enhanced Hover */}
            <div className="flex items-center justify-center gap-2">
              {testimonials.map((_, index) =>
              <button
                key={`dot-${index}`}
                onClick={() => handleDotClick(index)}
                className={`h-2 rounded-full transition-smooth hover:scale-125 ${
                index === activeIndex ? 'bg-primary w-8' : 'bg-muted-foreground/30 w-2 hover:bg-primary/50'}`
                }
                aria-label={`View testimonial ${index + 1}`}>
              </button>
              )}
            </div>
          </div>

          <div className="hidden md:block absolute right-24 top-1/2 -translate-y-1/4 -rotate-6 w-48 h-64 rounded-3xl overflow-hidden shadow-xl transition-smooth hover:scale-110 hover:rotate-0 hover:z-30">
            <AppImage
              src={testimonials[2].photo}
              alt={`${testimonials[2].name} from ${testimonials[2].company}`}
              className="w-full h-full object-cover grayscale hover:grayscale-0 transition-smooth" />
            
          </div>
          <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 rotate-12 w-48 h-64 rounded-3xl overflow-hidden shadow-xl transition-smooth hover:scale-110 hover:rotate-0 hover:z-30">
            <AppImage
              src={testimonials[1].photo}
              alt={`${testimonials[1].name} from ${testimonials[1].company}`}
              className="w-full h-full object-cover grayscale hover:grayscale-0 transition-smooth" />
            
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;